/**
 * Warm shared Pinia stores after the visible route has finished loading.
 *
 * The legacy application loaded these datasets once after authentication and
 * reused them on navigation. Small batches preserve that behavior without
 * making every database query and route chunk compete with the page the user
 * is currently waiting for.
 */
import { networkPendingRequests } from '@/services/http';

type PermissionCheck = (code: string) => boolean;

async function idleTurn(): Promise<void> {
  await new Promise<void>((resolve) => {
    const requestIdle = (window as Window & { requestIdleCallback?: Window['requestIdleCallback'] }).requestIdleCallback;
    if (typeof requestIdle === 'function') {
      requestIdle.call(window, () => resolve(), { timeout: 300 });
      return;
    }
    globalThis.setTimeout(resolve, 40);
  });
  while (networkPendingRequests.value > 0) {
    await new Promise((resolve) => globalThis.setTimeout(resolve, 60));
  }
}

async function runBatches(batches: Array<Array<() => Promise<unknown>>>): Promise<void> {
  for (const batch of batches) {
    if (!batch.length) continue;
    await Promise.allSettled(batch.map((load) => load()));
    await idleTurn();
  }
}

export async function preloadApplication(can: PermissionCheck): Promise<void> {
  const [dashboardModule, skuModule, inventoryModule, configModule, salesModule, settingsModule] =
    await Promise.all([
      import('@/features/dashboard/dashboard.store'),
      import('@/features/skus/skus.store'),
      import('@/features/inventory/inventory.store'),
      import('@/features/config/config.store'),
      import('@/features/sales/sales.store'),
      import('@/features/replenishment/settings.store'),
    ]);

  const dashboard = dashboardModule.useDashboardStore();
  const skus = skuModule.useSkusStore();
  const sales = salesModule.useSalesStore();
  const settings = settingsModule.useReplenishmentSettingsStore();
  const permitted = (permission: string, load: () => Promise<unknown>) => (can(permission) ? [load] : []);

  await runBatches([
    [
      ...permitted('function.dashboard.view', dashboard.ensureLoaded),
      ...permitted('function.skus.view', skus.ensureLoaded),
      ...permitted('function.warehouses.view', inventoryModule.useWarehousesStore().ensureLoaded),
    ],
    [
      ...permitted('function.inventory.view', inventoryModule.useInventoryStore().ensureLoaded),
      ...permitted('function.age.view', inventoryModule.useAgeStore().ensureLoaded),
      ...permitted('function.transit.view', inventoryModule.useTransitStore().ensureLoaded),
    ],
    [
      ...permitted('function.factory_orders.view', inventoryModule.useFactoryOrdersStore().ensureLoaded),
      ...permitted('function.ratios.view', configModule.useRatiosStore().ensureLoaded),
      ...permitted('function.activities.view', configModule.useActivitiesStore().ensureLoaded),
    ],
    [
      ...permitted('function.sales.view', sales.ensureLoaded),
      ...permitted('function.sales.view', sales.ensureWeeklyLoaded),
      ...permitted('function.replenishment.view', settings.ensureLoaded),
    ],
    [
      ...permitted('function.users.view', configModule.useUsersStore().ensureLoaded),
      ...permitted('function.audit.view', configModule.useAuditStore().ensureLoaded),
    ],
  ]);
}

export async function preloadRouteComponents(can: PermissionCheck): Promise<void> {
  const imports: Array<[string, () => Promise<unknown>]> = [
    ['menu.skus', () => import('@/features/skus/SkusPage.vue')],
    ['menu.inventory', () => import('@/features/inventory/InventoryPage.vue')],
    ['menu.age', () => import('@/features/inventory/AgePage.vue')],
    ['menu.transit', () => import('@/features/inventory/TransitPage.vue')],
    ['menu.factory_orders', () => import('@/features/inventory/FactoryOrdersPage.vue')],
    ['menu.factory_shippable', () => import('@/features/inventory/FactoryShippablePage.vue')],
    ['menu.warehouses', () => import('@/features/inventory/WarehousesPage.vue')],
    ['menu.sales', () => import('@/features/sales/SalesPage.vue')],
    ['menu.replenishment', () => import('@/features/replenishment/ReplenishmentPage.vue')],
    ['menu.warehouse_overview', () => import('@/features/replenishment/WarehouseOverviewPage.vue')],
    ['button.replenishment.settings', () => import('@/features/replenishment/ReplenishmentSettingsPage.vue')],
    ['menu.ratios', () => import('@/features/config/RatiosPage.vue')],
    ['menu.sku_groups', () => import('@/features/config/GroupsPage.vue')],
    ['menu.activities', () => import('@/features/config/ActivitiesPage.vue')],
    ['menu.help', () => import('@/features/config/HelpPage.vue')],
    ['menu.warehouse_analysis', () => import('@/features/analysis/WarehouseAnalysisPage.vue')],
    ['menu.users', () => import('@/features/config/UsersPage.vue')],
    ['menu.audit', () => import('@/features/config/AuditPage.vue')],
    ['menu.permissions', () => import('@/features/permissions/PermissionsPage.vue')],
  ];
  const permitted = imports.filter(([permission]) => can(permission)).map(([, load]) => load);
  const batches: Array<Array<() => Promise<unknown>>> = [];
  for (let index = 0; index < permitted.length; index += 3) {
    batches.push(permitted.slice(index, index + 3));
  }
  await runBatches(batches);
}
